import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import Loader from 'components/Loader/Loader';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReportService from 'services/report.service';
import { ReportType } from 'types/report.types';
import { notification } from 'utils/snackbar';

const reportService = new ReportService();

export type ReportData = {
  reporter: {
    id: number;
    shopId: number;
    shopSlug: string;
  };
  reported: {
    id: number;
    shopId: number;
    productId?: number;
    shopSlug: string;
  };
  reason: ReportType;
  comment?: string;
};

type Props = {
  reportData: ReportData;
  reportReasons?: ReportType[];
  opened: boolean;
  close: () => void;
};

const ReportDialog = (props: Props) => {
  const { reportData, opened, close, reportReasons } = props;
  const { t } = useTranslation();

  const [reasons, setReasons] = useState<ReportType[]>(reportReasons);

  const [reason, setReason] = useState<ReportType>({ name: null, type: null });
  const [descriptionLabel, setDescriptionLabel] = useState<string>(t('components.report.report-description-optional'));
  const [validation, setValidation] = useState({
    reason: true,
    description: true,
  });

  const [comment, setComment] = useState<string>('');

  const [isLoading, setIsLoading] = useState(false);

  const resetValidation = () => {
    setValidation({
      reason: true,
      description: true,
    });
  };

  const reset = () => {
    resetValidation();
    setReason({ name: null, type: null });
    setComment('');
  };

  const handleClose = () => {
    reset();
    close();
  };

  const sendReport = async () => {
    resetValidation();

    if (!reason) {
      setValidation({ ...validation, reason: false });

      return;
    }

    if (reason.type === 'other' && comment.trim() === '') {
      setValidation({ ...validation, description: false });

      return;
    }

    try {
      setIsLoading(true);

      const report = {
        ...reportData,
        reason,
        comment,
      };

      await reportService.createReport(report, { silentFail: true });

      setIsLoading(false);
      notification.showSuccessNotification(t('components.report.report-success'));
    } catch (error) {
      setIsLoading(false);

      const infoMessage = error?.data?.data?.message;

      if (error?.data?.data?.type === 'alreadyReported') {
        notification.showInfoNotification(infoMessage);

        return;
      }

      notification.showErrorNotification({
        message: t('components.report.save-error'),
        error,
      });
    } finally {
      handleClose();
    }
  };

  const loadReasons = async () => {
    setIsLoading(true);
    const loadedReasons = await reportService.getReportReasons();

    if (loadedReasons) {
      setReasons(loadedReasons);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!reportReasons || reportReasons?.length === 0) {
      loadReasons();
    }
  }, []);

  useEffect(() => {
    if (reason.type === 'other') {
      setDescriptionLabel(t('components.report.report-description'));

      return;
    }
    setDescriptionLabel(t('components.report.report-description-optional'));
  }, [reason]);

  return (
    <>
      <Dialog className="report-dialog" open={opened} onClose={handleClose}>
        <DialogTitle>{t('components.report.title')}</DialogTitle>
        <DialogContent className="report-dialog__content">
          <div className="report-dialog__content-wrapper mobile-full-width">
            {isLoading ? (
              <Loader />
            ) : (
              <>
                {/* REPORT REASON */}
                <FormControl error={!validation.reason}>
                  <FormLabel component="legend">{t('components.report.report-reason')}</FormLabel>
                  <RadioGroup value={reason.type}>
                    {reasons?.map((radioReason) => (
                      <FormControlLabel
                        key={radioReason.type}
                        className="report-dialog__radio-control"
                        control={<Radio color="primary" size="small" />}
                        value={radioReason.type}
                        label={radioReason.name}
                        onChange={() => {
                          resetValidation();
                          setReason(radioReason);
                        }}
                      />
                    ))}
                  </RadioGroup>
                  {!validation.reason && <FormHelperText>{t('components.report.reason-error')}</FormHelperText>}
                </FormControl>

                {/* REPORT COMMENT */}
                <TextField
                  variant="standard"
                  id="report-comment"
                  className="report-comment"
                  value={comment}
                  error={!validation.description}
                  onChange={(event) => setComment(event.target.value)}
                  margin="normal"
                  multiline={true}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label={descriptionLabel}
                  helperText={!validation.description && t('components.report.report-description-error')}
                />
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={sendReport}
            color="primary"
            disabled={isLoading || !reason.type || (reason.type === 'other' && comment.trim() === '')}
          >
            {t('common.send')}
          </Button>
          <Button variant="outlined" onClick={handleClose} color="primary" disabled={isLoading}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportDialog;
